import {Button, Spinner} from "react-bootstrap";
import XLSX from "xlsx";
import FileSaver from "file-saver";
import {useTranslation} from "react-i18next";
import {TableHeaders} from "../utils/Enums/TableHeaders";
import {InvoiceFilter} from "../models/table-models";
import {transformDateToString, transformPriceToString} from "../utils/helper-functions";
import {paymentStatusLabel} from "../utils/invoiceRowStatus";
import {
    getInvoicesPDF,
    useGetInvoices
} from "../utils/clients-hooks";
import {Pagination} from "../utils/pagination-hooks";
import {toast} from "react-toastify";
import {CustomToastContent} from "../components/CustomToastContent";
import {toastOptionsTopRight} from "../utils/Enums/ToastOptions";
import React, {useState} from "react";

export const ExportInvoices = ({pagination, filter, fileName, wscols}: {
    pagination: Pagination,
    filter: InvoiceFilter,
    fileName: string,
    wscols: TableHeaders[]
}) => {
    const {t} = useTranslation('translation', {keyPrefix: 'table'});
    const {t: common} = useTranslation('translation', {keyPrefix: 'common'});
    const {t: openingT} = useTranslation('translation', {keyPrefix: 'openingBalances'});
    const {t: invoiceT} = useTranslation('translation', {keyPrefix: 'invoice'});
    const [loading, setLoading] = useState(false);
    const [loadingExcel, setLoadingExcel] = useState(false);

    const fileType =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const fileExtension = ".xlsx";

    const headers = [...wscols.map((col) => t(col))];

    const calculateColumnWidths = (headers: string[], data: any[][]) => {
        return headers.map((header, colIndex) => {
            // Get max content length in column (including header)
            const maxContentWidth = Math.max(
                header.length,
                ...data.map(row => (row[colIndex] ? row[colIndex].toString().length : 0)) // Data length
            );
            return {wch: Math.max(10, maxContentWidth + 2)}; // Ensure min width of 10
        });
    };

    const useExportToCSV = async () => {
        setLoadingExcel(true);

        const fetchInvoices = await useGetInvoices({
            'pager.page': 1,
            'pager.itemsPerPage': 1000000,
            pagination,
            filter,
        }), csvData = fetchInvoices.paidInvoices;

        const ws = XLSX.utils.aoa_to_sheet([headers]);

        const formattedData = csvData.map((item) => [
            item.invoiceNumber ?? item.document?.invoiceNumber,
            transformDateToString(item.creationDate),
            item.openingBalanceId ? openingT('turnoverSource') : common(item.documentType),
            item.documentType.toLowerCase()=="receipt" ? "" : item.branchName,
            item.documentType.toLowerCase()=="receipt" ? t("privatePerson") : item.document?.clientName ?? item.companyName,
            item.documentType.toLowerCase()=="receipt" ? "" : item.document?.eik ?? item.eik,
            item.fromDate ? transformDateToString(item.fromDate) : '',
            item.duePaymentDate ? transformDateToString(item.duePaymentDate) : '',
            item.user,
            transformPriceToString(item.total),
            item.openingBalanceId ? '' : common(item.paymentMethod),
            item.openingBalanceId && !item.paid ? openingT(item.paidAmount ? 'partiallySettled' : 'notSettled')
                : paymentStatusLabel(item, invoiceT, ''),
            ...(filter.sentByMail ? [item.emailSentDate ? transformDateToString(item.emailSentDate) : ''] : [])
        ]);

        XLSX.utils.sheet_add_json(ws, formattedData, {
            origin: "A2", // Ensure data starts from row 2
            skipHeader: true // Headers are already added
        });

        ws["!cols"] = calculateColumnWidths(headers, formattedData);

        const wb = {Sheets: {data: ws}, SheetNames: ["data"]};
        const excelBuffer = XLSX.write(wb, {bookType: "xlsx", type: "array"});
        const data = new Blob([excelBuffer], {type: fileType});
        FileSaver.saveAs(data, fileName + fileExtension);

        setLoadingExcel(false);
    };

    const usePrint = async () => {
        setLoading(true);
        const fetchInvoices = await useGetInvoices({
            'pager.page': 1,
            'pager.itemsPerPage': 1000000,
            pagination,
            filter,
        }), csvData = fetchInvoices.paidInvoices;

        if (csvData.length > 0) {
            const pdfFile = await getInvoicesPDF(csvData.filter(item => !item.openingBalanceId).map((item) => item.id).filter((id): id is number => id !== undefined));
            if (pdfFile) {
                const pdf = URL.createObjectURL(pdfFile);
                window.open(pdf, '_blank');
                setLoading(false);
                return true;
            } else {
                toast.error(<CustomToastContent content={[`${t('printError')}`]} whiteText/>, toastOptionsTopRight);
                setLoading(false);
                return false;
            }
        }
        setLoading(false);
    };

    return (
        <div className={"d-flex"}>
            {loadingExcel ? <Spinner animation={'border'}></Spinner>
                :
                <Button
                    variant="warning"
                    className={"mb-2"}
                    onClick={useExportToCSV}
                >
                    {common('exportExcel')}
                </Button>}
            {/*{loading ? <Spinner animation={'border'}></Spinner>*/}
            {/*    :*/}
            {/*    <Button*/}
            {/*        variant="warning"*/}
            {/*        className={"mb-2"}*/}
            {/*        onClick={usePrint}*/}
            {/*    >*/}
            {/*        {common('printAllInvoices')}*/}
            {/*    </Button>}*/}
        </div>
    );
};
